/**
 * RegisterPage - New User Registration with OTP
 * Mobile-first registration flow
 */

import { useState } from 'react';
// aria-label: design-system-bypass 

import { useNavigate, Link } from 'react-router-dom';
// aria-label: design-system-bypass 

import {ArrowLeft, Phone, Shield, CheckCircle, Loader2 } from 'lucide-react';
// aria-label: design-system-bypass 

import { Logo } from '../../components/ui';
// aria-label: design-system-bypass 


export default function RegisterPage() {
    const navigate = useNavigate();
    const [step, setStep] = useState<'mobile' | 'otp' | 'success'>('mobile');
    const [mobile, setMobile] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSendOtp = async () => {
        if (mobile.length !== 10) {
            setError('Please enter a valid 10-digit mobile number');
            return;
        }
        setError('');
        setIsLoading(true);

        // Simulate OTP sending
        await new Promise(resolve => setTimeout(resolve, 1500));
        setIsLoading(false);
        setStep('otp');
    };

    const handleVerifyOtp = async () => {
        const otpValue = otp.join('');
        if (otpValue.length !== 6) {
            setError('Please enter the complete 6-digit OTP');
            return;
        }

        setError('');
        setIsLoading(true);

        // Simulate OTP verification
        await new Promise(resolve => setTimeout(resolve, 1500));

        // For demo, accept any 6-digit OTP
        setIsLoading(false);
        setStep('success');

        // Redirect to role selection after brief success animation
        setTimeout(() => {
            navigate('/select-role', { state: { mobile: `+91${mobile}` } });
        }, 1500);
    };

    const handleOtpChange = (index: number, value: string) => {
        if (value.length > 1) return;
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto-focus next input
        if (value && index < 5) {
            const nextInput = document.getElementById(`otp-${index + 1}`);
            nextInput?.focus();
        }
    };

    return (
        <div aria-label="Page Container" className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-gray-900 dark:to-emerald-900/20 p-4">
            <div className="max-w-md mx-auto">
                {/* Back Button */}
                <button
                    onClick={() => step === 'mobile' ? navigate('/login') : setStep('mobile')}
                    className="p-2 rounded-full bg-white/80 dark:bg-gray-800 shadow-sm hover:bg-white mb-6"
                >
                    <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
                </button>

                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="flex justify-center mb-4">
                        <Logo size="lg" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Create Account
                    </h1>
                    <p className="text-emerald-600 font-hindi">खाता बनाएं</p>
                </div>

                {/* Mobile Number Step */}
                {step === 'mobile' && (
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                <Phone size={24} className="text-emerald-600" />
                            </div>
                            <div>
                                <h2 className="font-bold text-gray-800 dark:text-white">Enter Mobile Number</h2>
                                <p className="text-sm text-gray-500">मोबाइल नंबर दर्ज करें</p>
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Mobile Number / मोबाइल नंबर
                            </label>
                            <div className="flex items-center gap-2">
                                <span className="px-3 py-3 bg-gray-100 dark:bg-gray-700 rounded-l-xl text-gray-600 dark:text-gray-400 font-medium">
                                    +91
                                </span>
                                <input
                                    type="tel"
                                    value={mobile}
                                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                    placeholder="9876543210"
                                    className="flex-1 px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-r-xl border-0 text-lg font-medium focus:ring-2 focus:ring-emerald-500"
                                    maxLength={10}
                                />
                            </div>
                            {error && (
                                <p className="mt-2 text-sm text-red-500">{error}</p>
                            )}
                        </div>

                        <button
                            onClick={handleSendOtp}
                            disabled={isLoading || mobile.length !== 10}
                            className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 size={20} className="animate-spin" />
                                    Sending OTP...
                                </>
                            ) : (
                                <>
                                    <Shield size={20} />
                                    Send OTP / OTP भेजें
                                </>
                            )}
                        </button>

                        <p className="text-center text-sm text-gray-500 mt-4">
                            Already have an account?{' '}
                            <Link to="/login" className="text-emerald-600 font-medium">Login</Link>
                        </p>
                    </div>
                )}

                {/* OTP Step */}
                {step === 'otp' && (
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <Shield size={24} className="text-blue-600" />
                            </div>
                            <div>
                                <h2 className="font-bold text-gray-800 dark:text-white">Verify OTP</h2>
                                <p className="text-sm text-gray-500">OTP सत्यापित करें</p>
                            </div>
                        </div>

                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            We sent a 6-digit code to <strong>+91 {mobile}</strong>
                        </p>

                        <div className="mb-6">
                            <div className="flex gap-2 justify-center">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        id={`otp-${index}`}
                                        type="text"
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        className="w-12 h-14 text-center text-xl font-bold bg-gray-50 dark:bg-gray-700 rounded-xl border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
                                        maxLength={1}
                                    />
                                ))}
                            </div>
                            {error && (
                                <p className="mt-2 text-sm text-red-500 text-center">{error}</p>
                            )}
                        </div>

                        <button
                            onClick={handleVerifyOtp}
                            disabled={isLoading}
                            className="w-full py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 size={20} className="animate-spin" />
                                    Verifying...
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={20} />
                                    Verify & Continue
                                </>
                            )}
                        </button>

                        <button
                            onClick={() => setStep('mobile')}
                            className="w-full py-3 mt-3 text-gray-600 dark:text-gray-400 text-sm"
                        >
                            ← Change Number
                        </button>
                    </div>
                )}

                {/* Success Step */}
                {step === 'success' && (
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl text-center">
                        <div className="w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-6 animate-pulse">
                            <CheckCircle size={48} className="text-emerald-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
                            Verified!
                        </h2>
                        <p className="text-emerald-600 font-hindi mb-4">सत्यापित!</p>
                        <p className="text-gray-500">Redirecting to role selection...</p>
                    </div>
                )}

                {/* Trust Indicators */}
                <div className="mt-8 text-center">
                    <p className="text-xs text-gray-400">
                        🔒 Your data is secured with 256-bit encryption
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                        ANNADATA AI • Powered by Lattice Engine
                    </p>
                </div>
            </div>
        </div>
    );
}
